import React from 'react';

// import { Profile, UpdateProfile } from '../../../interfaces';
import {
	BottomActionLayout,
	BottomActionLayoutProps,
} from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { Label } from '@/components/shared/Inputs/Label';
import { Select } from '@/components/shared/Inputs/Select';
import { ResponsiveBoxButtons } from '@/components/shared/ResponsiveBoxButtons';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAuth } from '@/providers/AuthProvider';
import { Formik, FormikErrors } from 'formik';
// import { S3Object } from 'models';
import * as Yup from 'yup';

import styles from './Serving.module.scss';

export interface ServingProps {
	onSave: () => void;
	onBack?: () => void;
	hasBack?: boolean;
	actionLabel?: string;
	bottomActionProps?: Partial<BottomActionLayoutProps>;
	leadingContent?: React.ReactElement;
}

type FormSchema = {
	role?: string | null;
	skills: string[];
};

const formValidationSchema: Yup.SchemaOf<FormSchema> = Yup.object().shape({
	role: Yup.string().nullable().required('Please select your role'),
	skills: Yup.array(Yup.string().required()),
	// spotifyId: Yup.string().required('Please enter a valid url'),
});

export const Serving: React.FC<ServingProps> = ({
	onSave,
	onBack,
	hasBack = false,
	actionLabel = 'Save',
	bottomActionProps,
	leadingContent,
}) => {
	// return null;
	const { user, updateUser } = useAuth();
	const { handleError } = useErrorHandler();
	// const [touched, setTouched] = useState(false);
	const initialValues: FormSchema = {
		role: user.role,
		skills: user.skills,
	};

	return (
		<Formik
			key="asaphSignUn"
			initialValues={initialValues}
			validationSchema={formValidationSchema}
			validateOnMount
			onSubmit={async (values: FormSchema) => {
				try {
					await updateUser({
						input: {
							role: values.role?.trim() || null,
							skills: values.skills || [],
						},
					});
					onSave();
				} catch (error) {
					handleError({
						errorCode: 'UpdateProfileError',
						friendlyMessage: 'Failed to update profile',
						error,
					});
					return;
				}
			}}
		>
			{({
				handleSubmit,
				handleChange,
				setFieldValue,
				values,
				errors,
				isValid,
			}) => (
				<BottomActionLayout
					{...bottomActionProps}
					actionContent={
						<>
							{hasBack ? (
								<AsaphButton
									color="grey"
									label="Back"
									onClick={() => onBack?.()}
									fullWidth
								/>
							) : null}
							<AsaphButton
								color="blue"
								label={actionLabel}
								disabled={!isValid}
								onClick={() => handleSubmit()}
								fullWidth
							/>
						</>
					}
				>
					<form
						onSubmit={handleSubmit}
						id="asaphSignIn"
						className={styles['root']}
					>
						{leadingContent}
						<Label>Your role <span style={{ color: 'var(--px-color-error, #e53e3e)' }}>*</span></Label>
						<Select
							placeholder="Select your role"
							items={[
								{
									label: 'Team member',
									value: 'Team member',
								},
								{
									label: 'Worship leader',
									value: 'Worship leader',
								},
								{
									label: 'Worship pastor',
									value: 'Worship pastor',
								},
								{
									label: 'Pastoral staff',
									value: 'Pastoral staff',
								},
								{
									label: 'Lead pastor',
									value: 'Lead pastor',
								},
								{
									label: 'Other',
									value: 'Other',
								},
							]}
							onChange={(value) => {
								// setTouched(true);
								setFieldValue('role', value);
							}}
							value={values.role || ''}
						/>

						<div className="p-sm" />
						<Label>Your skill (select any that apply)</Label>
						<ResponsiveBoxButtons
							items={generateResponsiveBoxButtons(
								[
									{
										src: 'vocals.svg',
										value: 'vocals',
										label: 'Vocals',
									},
									{
										src: 'acoustic.png',
										value: 'acoustic',
										label: 'Acoustic',
									},
									{
										src: 'keys.svg',
										value: 'keys',
										label: 'Keys',
									},
								],
								setFieldValue,
								values.skills,
							)}
						/>
						<ResponsiveBoxButtons
							className="mt-md"
							items={generateResponsiveBoxButtons(
								[
									{
										src: 'electric.svg',
										value: 'electric',
										label: 'Electric',
									},
									{
										src: 'drums.svg',
										value: 'drums',
										label: 'Drums',
									},
									{
										src: 'bass.png',
										value: 'bass',
										label: 'Bass',
									},
								],
								setFieldValue,
								values.skills,
							)}
						/>
						<ResponsiveBoxButtons
							className="mt-md"
							items={generateResponsiveBoxButtons(
								[
									{
										src: 'strings.svg',
										value: 'strings',
										label: 'Strings',
									},
									{
										src: 'wind.svg',
										value: 'wind',
										label: 'Wind/Brass',
									},
									{
										src: 'production.svg',
										value: 'production',
										label: 'Production',
									},
								],
								setFieldValue,
								values.skills,
							)}
						/>
					</form>
				</BottomActionLayout>
			)}
		</Formik>
	);
};

const generateResponsiveBoxButtons = (
	instruments: {
		value: string;
		src: string;
		label: string;
	}[],
	setFieldValue: (
		field: string,
		value: string[],
	) => Promise<void | FormikErrors<FormSchema>>,
	currentValue: string[],
) => {
	return instruments.map(({ src, value, label }) => ({
		content: (
			<img width={48} height={48} src={`/assets/instruments/${src}`} />
		),
		label,
		selected: currentValue.includes(value),
		onClick: async () => {
			const newValue = currentValue.includes(value)
				? currentValue.filter((v) => v !== value)
				: [...currentValue, value];
			await setFieldValue('skills', newValue);
		},
	}));
};

export default Serving;
